import { useQuery } from "gemi/client";

/**
 * Session state for the v2 landing surface. Reads `/public/session`, the non-erroring twin of
 * `/auth/me`: it answers 200 for anonymous and signed-in visitors alike, so the read never
 * errors and gemi never arms its retry-on-error loop — which is what made the nav CTA flash
 * when the landing polled the 401ing `/auth/me`.
 *
 * `/` has no auth middleware and a browser may hold it from before sign-in, so this legitimately
 * starts as `loading` with no user. `suspense: false` keeps the page from suspending on the read.
 * The resolved value survives background refetches (so the CTA never falls back to its placeholder
 * after the first resolve) because gemi silently revalidates a variant that already has data and
 * never rewrites `loading` on that path; `keepPreviousData: true` is gemi's default and is kept
 * explicit only to pin the behaviour against a future app-level `queryConfig`. gemi dedupes by
 * path, so every landing consumer shares the one request.
 *
 * The read keys on `/public/session`, while gemi's `useSignIn`/`useSignOut` invalidate `/auth/me`.
 * That is harmless today because every sign-in/out from the landing ends in a hard location change
 * that resets the client store: the auth modal soft-navigates to `/app/redirect`, which then does a
 * `window.location.href` hop (CustomerRedirect), and sign-out reloads. If a client-side nav ever
 * returns to `/` right after a sign-out, this key can show stale data until `staleTime` elapses.
 */
export function useLandingSession() {
  const { data, loading } = useQuery(
    "/public/session",
    {},
    { suspense: false, keepPreviousData: true },
  );
  return { user: data?.user ?? null, loading };
}
